import React, { useCallback, useLayoutEffect, useRef, useState } from "react";

export default function AlertScrollArea({ children, className = "", viewportClassName = "", viewportRef: externalViewportRef }) {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const [metrics, setMetrics] = useState({ visible: false, height: 0, top: 0 });

  const setViewportRef = useCallback((node) => {
    viewportRef.current = node;
    if (typeof externalViewportRef === "function") externalViewportRef(node);
    else if (externalViewportRef) externalViewportRef.current = node;
  }, [externalViewportRef]);

  const updateMetrics = useCallback(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const { clientHeight, scrollHeight, scrollTop } = viewport;
    const trackHeight = track.clientHeight;
    if (scrollHeight <= clientHeight || !trackHeight) {
      setMetrics((prev) =>
        prev.visible || prev.height || prev.top
          ? { visible: false, height: 0, top: 0 }
          : prev
      );
      return;
    }

    const height = Math.max(36, (clientHeight / scrollHeight) * trackHeight);
    const top = (scrollTop / (scrollHeight - clientHeight)) * (trackHeight - height);
    setMetrics({ visible: true, height, top });
  }, []);

  useLayoutEffect(() => {
    updateMetrics();
    const resizeObserver = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateMetrics) : null;
    if (viewportRef.current) resizeObserver?.observe(viewportRef.current);
    window.addEventListener("resize", updateMetrics);
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateMetrics);
    };
  }, [updateMetrics]);

  return (
    <div className={`crm-alert-scroll-area ${metrics.visible ? "has-scroll" : ""} ${className}`.trim()}>
      <div ref={setViewportRef} className={`crm-alert-scroll-area__viewport ${viewportClassName}`.trim()} onScroll={updateMetrics}>
        {children}
      </div>
      <span ref={trackRef} className={`crm-alert-scroll-area__track ${metrics.visible ? "is-visible" : ""}`} aria-hidden="true">
        <span className="crm-alert-scroll-area__thumb" style={{ height: metrics.height, transform: `translateY(${metrics.top}px)` }} />
      </span>
    </div>
  );
}
